// adminUsersApi — adaptador del directorio de NEGOCIOS del super_admin sobre lealify-base.
// Reemplaza ApiService.fetchAdminUsersPage de ElasticsearchService2 (esapi/Elasticsearch).
// Llama GET /admin/users-with-counts (ver routes/admin.js) y mapea fechas a Date, igual que
// hacía el servicio viejo, para que AdminUsersPage no cambie su lógica de agrupado/render.
import { baseApi } from './baseClient';
import type { PlanType } from '../types/user';

// Verticales de la plataforma (espejo de VALID_VERTICALS en lealify-base/lib/verticals.js).
export type Vertical = 'lealtad' | 'dental' | 'letsmove' | 'clublectura' | 'pos';

// ── Tipos (espejo de ElasticsearchService2.ts:28-65) ──
export interface AdminUserFilters {
  term?: string;
  plan?: PlanType | 'all';
  status?: 'active' | 'expired' | 'cancelled' | 'all';
  hasBusiness?: 'true' | 'false' | 'all';
  vertical?: Vertical | 'all';
  dateFrom?: Date | string | null;
  dateTo?: Date | string | null;
  expirationStatus?: 'all' | 'expired' | 'expiring-soon' | 'active';
  /** Solo periodos de PRUEBA (deep-link `?trial=1` desde la tarjeta «pruebas vencen esta semana»). */
  onlyTrial?: boolean;
}

export interface SubscriptionData {
  plan: PlanType;
  startDate: Date | null;
  endDate?: Date | null;
  status: 'active' | 'expired' | 'cancelled';
  paymentMethod?: string | null;
}

export interface UserData {
  uid: string;
  ownerUserId?: number | null;  // users.id del owner (botón "Ver como")
  email: string;
  displayName: string;
  businessName: string | null;
  businessId: string | null;
  // Vertical del negocio (de tenants.vertical). Default 'lealtad' si viniera nula.
  vertical: Vertical;
  // ¿tiene punto de venta enlazado? Va APARTE de la vertical: el POS no sustituye al giro
  // (Snowtea es de lealtad y además tiene POS), por eso no es un valor más de `vertical`.
  hasPos?: boolean;
  role: string;
  // El adaptador siempre devuelve Date (o null) — el page hace .getTime()/formatDate sobre esto.
  createdAt: Date | null;
  phoneNumber?: string | null;
  avatarUrl?: string | null;    // foto del dueño (propia o de WhatsApp), si la tenemos
  // base no separa lada del teléfono → countryCode neutro ('') para ClientContactInfo.
  countryCode?: string | null;
  subscription?: SubscriptionData | null;
  clientCount: number;
  // base aún no expone "última actividad" del negocio → el chip cae a "Sin actividad".
  lastActivityAt?: string | number | Date | null;
}

export interface AdminUsersResponse {
  items: UserData[];
  total: number;
  page: number;
  size: number;
  hasMore: boolean;
}

// Fecha local → 'YYYY-MM-DD' para los params dateFrom/dateTo (sin desfase de zona).
function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

interface RawSubscription {
  plan: PlanType;
  startDate: string | null;
  endDate: string | null;
  status: 'active' | 'expired' | 'cancelled';
  paymentMethod: string | null;
}
interface RawUser {
  uid: string;
  ownerUserId?: number | null;
  email: string;
  displayName: string;
  businessName: string | null;
  businessId: string | null;
  vertical: Vertical | null;
  hasPos?: boolean;
  role: string;
  createdAt: string | null;
  lastActivityAt: string | null;
  phoneNumber: string | null;
  avatarUrl?: string | null;
  clientCount: number;
  subscription: RawSubscription | null;
}

export const adminUsersApi = {
  // Página del directorio de negocios. filters/page/size → query params del endpoint base.
  fetchUsersPage: async (
    filtersOrTerm: AdminUserFilters | string = '',
    page = 1,
    size = 20,
  ): Promise<AdminUsersResponse> => {
    const filters: AdminUserFilters =
      typeof filtersOrTerm === 'string' ? { term: filtersOrTerm } : filtersOrTerm;

    const p = new URLSearchParams();
    if (filters.term?.trim()) p.set('term', filters.term.trim());
    if (filters.plan && filters.plan !== 'all') p.set('plan', filters.plan);
    if (filters.status && filters.status !== 'all') p.set('status', filters.status);
    if (filters.hasBusiness && filters.hasBusiness !== 'all') p.set('hasBusiness', filters.hasBusiness);
    if (filters.vertical && filters.vertical !== 'all') p.set('vertical', filters.vertical);
    if (filters.dateFrom) {
      const d = filters.dateFrom instanceof Date ? filters.dateFrom : new Date(filters.dateFrom);
      if (!isNaN(d.getTime())) p.set('dateFrom', ymd(d));
    }
    if (filters.dateTo) {
      const d = filters.dateTo instanceof Date ? filters.dateTo : new Date(filters.dateTo);
      if (!isNaN(d.getTime())) p.set('dateTo', ymd(d));
    }
    p.set('page', String(page));
    p.set('size', String(size));

    const data = await baseApi.get<{
      items: RawUser[]; total: number; page: number; size: number; hasMore: boolean;
    }>(`/admin/users-with-counts?${p.toString()}`);

    const items: UserData[] = (data.items || []).map((u) => ({
      uid: u.uid,
      ownerUserId: u.ownerUserId ?? null,
      email: u.email,
      displayName: u.displayName,
      businessName: u.businessName,
      businessId: u.businessId,
      vertical: u.vertical ?? 'lealtad',
      hasPos: !!u.hasPos,
      role: u.role,
      createdAt: u.createdAt ? new Date(u.createdAt) : null,
      phoneNumber: u.phoneNumber ?? null,
      avatarUrl: u.avatarUrl ?? null,   // foto del dueño (propia o de WhatsApp): sin esto el avatar nunca llegaba a la fila
      countryCode: '',
      clientCount: u.clientCount ?? 0,
      subscription: u.subscription
        ? {
            plan: u.subscription.plan,
            status: u.subscription.status,
            startDate: u.subscription.startDate ? new Date(u.subscription.startDate) : null,
            endDate: u.subscription.endDate ? new Date(u.subscription.endDate) : null,
            paymentMethod: u.subscription.paymentMethod ?? null,
          }
        : null,
      lastActivityAt: u.lastActivityAt ?? null,
    }));

    return { items, total: data.total || 0, page: data.page || page, size: data.size || size, hasMore: !!data.hasMore };
  },
};

export default adminUsersApi;

// "Ver como" de plataforma (Fase A: mismo dominio — el manager se recarga como ese usuario).
export const impersonateUser = (userId: number) =>
  baseApi.post<{ ok: true; viewing: { id: number; name: string; role: string }; tenant: { id: number; name: string } }>(
    '/admin/impersonate', { user_id: userId });

// Fase B: fachadas en OTRO dominio (letsmove, agenda dental, club...). Devuelve la URL de
// canje de un solo uso (60s) que se abre en pestaña nueva para sembrar la cookie allá.
export const impersonationCode = (userId: number) =>
  baseApi.post<{ ok: true; url: string; host: string; viewing: { id: number; name: string; role: string } }>(
    '/admin/impersonation-code', { user_id: userId });
